/*
  Custom Select
  - Handles the custom select
  - Keeps custom options in sync with native select changes
*/
class IaCustomSelect extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector("[data-select-trigger]");
    this.valueSpan = this.querySelector("[data-select-value]");
    this.optionsContainer = this.querySelector(".ia-custom-options");
    this.customSelect = this.querySelector(".ia-custom-select");
    this.nativeSelect = this.querySelector("select");
    this.form = this.closest("form");
    this.labelText = this.getAttribute("data-default-value") || "";

    this.setupAccessibility();

    // Initial render of options
    this.renderOptions();

    // Close select when clicking outside
    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.customSelect.classList.remove("open");
        this.syncExpanded();
      }
    });

    // Open/close trigger
    this.addEventListener("click", (e) => {
      if (this.trigger?.contains(e.target)) {
        this.isOpen ? this.closeList(false) : this.openList();
      } else if (this.isOpen && e.target.closest?.("[data-select-option]")) {
        this.closeList();
      } else {
        this.customSelect.classList.toggle("open");
        this.syncExpanded();
      }
    });

    this.trigger?.addEventListener("keydown", this.onTriggerKeydown.bind(this));
    this.optionsContainer?.addEventListener("keydown", this.onOptionsKeydown.bind(this));

    // Listen for option click events (delegated for re-rendered options)
    this.optionsContainer?.addEventListener("click", (e) => {
      const option = e.target.closest("[data-select-option]");
      if (option) {
        const value = option.dataset.value;
        this.setValue(value);
        this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }
    });

    // Keep value in sync if user changes native select
    this.nativeSelect?.addEventListener("change", () => {
      this.setValue(this.nativeSelect.value);
    });

    // Reset value on form reset
    this.form?.addEventListener("reset", () => {
      setTimeout(() => {
        this.setValue(this.nativeSelect.options[0]?.value || "");
      }, 0);
    });

    // MutationObserver: watch for changes in the native select's options/text
    if (this.nativeSelect) {
      this.__observer = new MutationObserver(() => {
        this.renderOptions();
        this.setValue(this.nativeSelect.value);
      });
      this.__observer.observe(this.nativeSelect, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  setupAccessibility() {
    if (!this.trigger || !this.optionsContainer) return;

    const uid = `ia-custom-select-${Math.random().toString(36).slice(2, 9)}`;
    this.optionsContainer.id = `${uid}-listbox`;
    this.optionsContainer.setAttribute("role", "listbox");
    this.optionsContainer.setAttribute("aria-label", this.labelText);

    this.trigger.setAttribute("role", "button");
    this.trigger.setAttribute("tabindex", "0");
    this.trigger.setAttribute("aria-haspopup", "listbox");
    this.trigger.setAttribute("aria-controls", this.optionsContainer.id);
    this.trigger.setAttribute("aria-expanded", "false");
    this.trigger.querySelector("svg")?.setAttribute("aria-hidden", "true");
    this.updateTriggerLabel();
  }

  get isOpen() {
    return this.customSelect?.classList.contains("open");
  }

  syncExpanded() {
    this.trigger?.setAttribute("aria-expanded", this.isOpen ? "true" : "false");
  }

  updateTriggerLabel() {
    if (!this.trigger) return;
    const selected = this.nativeSelect?.selectedOptions?.[0];
    const selectedText = selected && selected.value !== "" ? selected.textContent.trim() : "All";
    this.trigger.setAttribute("aria-label", `${this.labelText}: ${selectedText}`);
  }

  getEnabledOptions() {
    return Array.from(this.optionsContainer?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') || []);
  }

  announce(message) {
    const status = this.closest("ia-filter-component")?.querySelector("[data-ia-filter-results-status-desktop]");
    if (!status) return;
    status.textContent = "";
    clearTimeout(this.announceTimer);
    if (!message) return;
    this.announceTimer = setTimeout(() => {
      status.textContent = message;
    }, 600);
  }

  openList() {
    this.customSelect.classList.add("open");
    this.syncExpanded();
    this.announce(`${this.labelText} filter options. Use arrow keys to move, Enter to apply, Escape to close.`);
    const options = this.getEnabledOptions();
    const target = options.find((option) => option.getAttribute("aria-selected") === "true") || options[0];
    target?.focus({ preventScroll: true });
  }

  closeList(returnFocus = true) {
    this.customSelect.classList.remove("open");
    this.syncExpanded();
    this.announce("");
    if (returnFocus) this.trigger?.focus({ preventScroll: true });
  }

  onTriggerKeydown(event) {
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      if (this.isOpen && (event.key === "Enter" || event.key === " ")) {
        this.closeList();
      } else {
        this.openList();
      }
    } else if (event.key === "Escape" && this.isOpen) {
      event.preventDefault();
      this.closeList();
    }
  }

  onOptionsKeydown(event) {
    const options = this.getEnabledOptions();
    const index = options.indexOf(document.activeElement);

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        options[Math.min(index + 1, options.length - 1)]?.focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        options[Math.max(index - 1, 0)]?.focus();
        break;
      case "Home":
        event.preventDefault();
        options[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        options[options.length - 1]?.focus();
        break;
      case "Enter":
      case " ": {
        event.preventDefault();
        const option = document.activeElement?.closest?.('[role="option"]');
        if (!option || option.getAttribute("aria-disabled") === "true") return;
        this.closeList();
        this.setValue(option.dataset.value);
        this.nativeSelect.dispatchEvent(new Event("change", { bubbles: true }));
        break;
      }
      case "Escape":
        event.preventDefault();
        this.closeList();
        break;
      case "Tab":
        this.closeList(false);
        break;
    }
  }

  escapeAttribute(value) {
    return String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }

  // Render custom options to reflect the native select
  renderOptions() {
    if (!this.optionsContainer || !this.nativeSelect) return;
    // If there's an "All" or empty value, render as the first option
    let customOptionsHtml = '';
    // Find options in native select
    const nativeOptions = Array.from(this.nativeSelect.options);

    // By design, first option is label (disabled), second is "All" (value="")
    for (let opt of nativeOptions) {
      customOptionsHtml += `<span class="ia-custom-option${opt.selected ? ' selected' : ''}${opt.disabled ? ' disabled' : ''}" data-select-option data-value="${opt.value}" role="option" tabindex="-1" aria-label="${this.escapeAttribute(opt.textContent.trim())}" aria-selected="${opt.selected ? 'true' : 'false'}"${opt.disabled ? ' aria-disabled="true"' : ''}>${opt.textContent}</span>`;
    }
    this.optionsContainer.innerHTML = customOptionsHtml;
  }

  setValue(value) {
    // Update the value display and underlying select
    // Find the native option text for this value
    let displayText = this.getAttribute("data-default-value") || "";
    if (this.nativeSelect) {
      const nativeOption = Array.from(this.nativeSelect.options).find(opt => opt.value === value);
      if (nativeOption) displayText = nativeOption.textContent;
      this.nativeSelect.value = value;
    }
    this.valueSpan.textContent = displayText;
    this.updateTriggerLabel();
    // Optional: visually mark selected option in the custom options
    if (this.optionsContainer) {
      Array.from(this.optionsContainer.children).forEach(optEl => {
        if (optEl.getAttribute("data-value") === value) {
          optEl.classList.add("selected");
          optEl.setAttribute("aria-selected", "true");
        } else {
          optEl.classList.remove("selected");
          optEl.setAttribute("aria-selected", "false");
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.__observer) this.__observer.disconnect();
  }
}

if (!customElements.get('ia-custom-select')) {
  customElements.define('ia-custom-select', IaCustomSelect);
}